/* eslint-disable max-len */
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button } from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ColorizeIcon from '@mui/icons-material/Colorize';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import style from './theme.module.scss';
import { SidebarThemesFormInput } from '../addCustomer/utils/types';
import { defaultSidebarColorDetails, SidebarThemesFormValue } from '../addCustomer/utils/helper';
import { RootState } from '../../redux/rootState';
import { setMySidebarStyles, updateSidebarStyles } from '../Profile/Redux/sliceMyProfile';
import ConfirmationModal from '../../Common/ConfirmationModal/ConfirmationModal';
import { ConfirmationIcon } from '../../assets/Icons/AnalystIcons/ConfirmationIcon';
import { setLoader } from '../../redux/loaderSlice';
import { getTextColorAsPerBackground } from '../../utils/CommonFunctions';

const iconOptions = [
  AppsIcon,
  CalendarMonthIcon,
  BusinessCenterIcon,
  BarChartIcon,
  DesktopWindowsIcon,
  ImageOutlinedIcon,
  ViewInArIcon,
  PanToolAltIcon,
];

const getSavedSidebarStyles = (
  sidebarStylesData: {
    TextColor?: string;
    BackgroundColor?: string;
    SecondBackgroundColor?: string;
    SelectionColor?: string;
    IsDark?: boolean;
  },
) => {
  let sidebarImageSet = defaultSidebarColorDetails.imageSet;

  if (sidebarStylesData?.IsDark !== undefined) {
    sidebarImageSet = sidebarStylesData.IsDark ? 'black' : 'white';
  }

  return {
    backgroundColor: sidebarStylesData?.BackgroundColor || defaultSidebarColorDetails.backgroundColor,
    textColor: sidebarStylesData?.TextColor || defaultSidebarColorDetails.textColor,
    secondBackgroundColor: sidebarStylesData?.SecondBackgroundColor || defaultSidebarColorDetails.secondBackgroundColor,
    selectedBackgroundColor: sidebarStylesData?.SelectionColor || defaultSidebarColorDetails.selectedBackgroundColor,
    imageSet: sidebarImageSet,
  };
};

function SidebarThemes() {
  const { mySidebarStyles, sidebarStylesData, isLoading } = useSelector((state: RootState) => state.userProfileReducer);
  const [selectedIconSet, setSelectedIconSet] = useState(0);
  const [selectedStyle, setSelectedStyle] = useState(1);
  const [openConfirmation, setOpenConfirmation] = useState('');
  const [isChanged, setIsChanged] = useState(false);
  const [saveValues, setSaveValues] = useState({} as SidebarThemesFormInput);
  const sidebarStylesDataRef = useRef(sidebarStylesData);

  const dispatch = useDispatch();

  const {
    handleSubmit, setValue,
  } = useForm<SidebarThemesFormInput>({
  });

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  useEffect(() => {
    sidebarStylesDataRef.current = sidebarStylesData;
  }, [sidebarStylesData]);

  const onSubmit = (values: SidebarThemesFormInput) => {
    setSaveValues(values);
    setOpenConfirmation('save');
  };

  const updateIsChanged = (nextSidebarStyles = mySidebarStyles) => {
    const savedSidebarStyles = getSavedSidebarStyles(sidebarStylesData);

    setIsChanged(
      nextSidebarStyles?.backgroundColor !== savedSidebarStyles.backgroundColor
      || nextSidebarStyles?.textColor !== savedSidebarStyles.textColor
      || nextSidebarStyles?.secondBackgroundColor !== savedSidebarStyles.secondBackgroundColor
      || nextSidebarStyles?.selectedBackgroundColor !== savedSidebarStyles.selectedBackgroundColor
      || nextSidebarStyles?.imageSet !== savedSidebarStyles.imageSet,
    );
  };

  const handleSelectedItemColorChange = (color: string) => {
    const nextSidebarStyles = {
      ...mySidebarStyles,
      selectedBackgroundColor: color,
    };

    setValue(SidebarThemesFormValue.Selected_Item_Color, color);
    updateIsChanged(nextSidebarStyles);
    dispatch(setMySidebarStyles(nextSidebarStyles));
  };

  const handlePenColorChange = (color: string) => {
    const nextSidebarStyles = {
      ...mySidebarStyles,
      textColor: color,
    };

    setValue(SidebarThemesFormValue.Pen_Color, color);
    updateIsChanged(nextSidebarStyles);
    dispatch(setMySidebarStyles(nextSidebarStyles));
  };

  const handleBackgroundColorChange = (color: string) => {
    const nextSidebarStyles = {
      ...mySidebarStyles,
      backgroundColor: color,
    };

    setValue(SidebarThemesFormValue.Background_Color, color);
    updateIsChanged(nextSidebarStyles);
    dispatch(setMySidebarStyles(nextSidebarStyles));
  };
  const handleSecondBackgroundColorChange = (color: string) => {
    const nextSidebarStyles = {
      ...mySidebarStyles,
      secondBackgroundColor: color,
    };

    setValue(SidebarThemesFormValue.Second_Background_Color, color);
    updateIsChanged(nextSidebarStyles);
    dispatch(setMySidebarStyles(nextSidebarStyles));
  };

  useEffect(() => {
    setValue(SidebarThemesFormValue.Selected_Item_Color, mySidebarStyles?.selectedBackgroundColor);
    setValue(SidebarThemesFormValue.Pen_Color, mySidebarStyles?.textColor);
    setValue(SidebarThemesFormValue.Background_Color, mySidebarStyles?.backgroundColor);
    setValue(SidebarThemesFormValue.Second_Background_Color, mySidebarStyles?.secondBackgroundColor);
    setSelectedIconSet(mySidebarStyles?.imageSet === 'black' ? 2 : 1);
  }, [mySidebarStyles]);

  useEffect(() => () => {
    // setOpenConfirmation('erase');
    dispatch(setMySidebarStyles(getSavedSidebarStyles(sidebarStylesDataRef.current)));
  }, []);

  const handleSave = () => {
    setOpenConfirmation('');
    setIsChanged(false);
    const payload = {
      BackgroundColor: saveValues.BackgroundColor,
      SecondBackgroundColor: saveValues.SecondBackgroundColor,
      TextColor: saveValues.PenColor,
      SelectionColor: saveValues.SelectedItemColor,
      IsDark: selectedIconSet === 2,
    };
    dispatch(updateSidebarStyles(payload));
  };

  const handleReset = () => {
    setOpenConfirmation('');
    setIsChanged(false);
    const payload = {
      BackgroundColor: defaultSidebarColorDetails.backgroundColor,
      SecondBackgroundColor: defaultSidebarColorDetails.secondBackgroundColor,
      TextColor: defaultSidebarColorDetails.textColor,
      SelectionColor: defaultSidebarColorDetails.selectedBackgroundColor,
      IsDark: defaultSidebarColorDetails.imageSet === 'black',
    };
    dispatch(updateSidebarStyles(payload));
  };

  const renderColorField = (
    title: string,
    description: string,
    color: string,
    onChange: (color: string) => void,
  ) => (
    <section className={style.settings_section}>
      <h3>{title}</h3>
      <p>{description}</p>
      <label className={style.color_input_row}>
        <span className={style.color_swatch} style={{ backgroundColor: color }} />
        <span className={style.color_value}>{color?.toUpperCase()}</span>
        <ColorizeIcon className={style.color_icon} />
        <input
          type="color"
          value={color}
          onChange={(event) => onChange(event.target.value)}
          aria-label={title}
        />
      </label>
    </section>
  );

  const renderIconSet = (setNumber: number) => (
    <button
      type="button"
      className={`${style.option_card} ${selectedIconSet === setNumber ? style.option_card_active : ''}`}
      onClick={() => {
        const nextSidebarStyles = {
          ...mySidebarStyles,
          imageSet: setNumber === 2 ? 'black' : 'white',
        };

        setSelectedIconSet(setNumber);
        updateIsChanged(nextSidebarStyles);
        dispatch(setMySidebarStyles(nextSidebarStyles));
      }}
    >
      <span className={style.radio_icon}>
        {selectedIconSet === setNumber ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
      </span>
      <span className={style.option_content}>
        <span className={style.option_title}>
          Icon Set
          {' '}
          {setNumber}
          {' '}
          {setNumber === 2 ? '(Black Icons)' : '(White Icons)'}
        </span>
        <span className={`${style.icon_preview_row} ${setNumber === 2 ? style.icon_preview_solid : ''}`}>
          {iconOptions.map((IconComponent, iconIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <IconComponent key={`${setNumber}-${iconIndex}`} />
          ))}
        </span>
      </span>
    </button>
  );

  const getConfirmationText = (key: string) => {
    switch (key) {
      case 'save':
        return 'Are you sure you want to save these sidebar theme changes?';
      case 'reset':
        return 'Are you sure you want to restore the sidebar theme changes?';
      // case 'erase':
      //   return 'Are you sure you want to discard unsaved changes?';
      default:
        return '';
    }
  };
  const getSuccessRun = (key: string) => {
    switch (key) {
      case 'save':
        return handleSave;
      case 'reset':
        return handleReset;
      // case 'erase':
      //   return () => { setOpenConfirmation(''); };
      default:
        return () => { setOpenConfirmation(''); };
    }
  };

  return (
    <div className={style.sidebar_theme_page}>
      <form className={style.sidebar_theme_form} onSubmit={handleSubmit(onSubmit)}>
        <div className={style.page_header}>
          <div>
            <h2>Customize Sidebar</h2>
            <p>Personalize the look and feel of the navigation menu.</p>
          </div>
          <div className={style.header_actions}>
            <Button type="button" className={style.reset_button} onClick={() => setOpenConfirmation('reset')}>
              <RestartAltIcon />
              Reset to Default
            </Button>
            <Button type="submit" className={style.save_button}>
              <CheckCircleOutlineIcon />
              Save Changes
            </Button>
          </div>
        </div>

        <div className={`${style.info_banner} ${isChanged ? style.attention_banner : ''}`}>
          <InfoOutlinedIcon />
          <span>
            {isChanged
              ? 'You have unsaved theme changes. Please save them before leaving, or your changes will be lost.'
              : 'The changes you make here will be applied to the sidebar across the application.'}
          </span>
        </div>

        <div className={style.settings_panel}>
          {renderColorField(
            '1. Sidebar Background Color',
            'Choose the background color for the entire sidebar.',
            mySidebarStyles?.backgroundColor,
            handleBackgroundColorChange,
          )}

          {renderColorField(
            '2. Card Background Color',
            'Choose the background color for the entire sidebar.',
            mySidebarStyles?.secondBackgroundColor,
            handleSecondBackgroundColorChange,
          )}

          {renderColorField(
            '3. Text Color',
            'Choose the default text color for all menu items.',
            mySidebarStyles?.textColor,
            handlePenColorChange,
          )}

          <section className={style.settings_section}>
            <h3>4. Icon Set</h3>
            <p>Choose a predefined icon set for the sidebar.</p>
            <div className={style.option_grid}>
              {renderIconSet(1)}
              {renderIconSet(2)}
            </div>
          </section>

          <section className={style.settings_section}>
            <h3>5. Selected Item Style</h3>
            <p>Choose how the selected menu item will look.</p>
            <div className={style.selected_style_grid}>
              <button
                type="button"
                className={`${style.option_card} ${selectedStyle === 1 ? style.option_card_active : ''}`}
                onClick={() => setSelectedStyle(1)}
              >
                <span className={style.radio_icon}>
                  {selectedStyle === 1 ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                </span>
                <span
                  className={style.selected_item_preview}
                  style={{ backgroundColor: mySidebarStyles?.selectedBackgroundColor, color: getTextColorAsPerBackground(mySidebarStyles?.selectedBackgroundColor) }}
                >
                  <span className={style.preview_dot} />
                  My Reports
                </span>
                <span className={style.style_label}>
                  <strong>Style 1</strong>
                  Solid Background
                </span>
              </button>
            </div>
          </section>

          {renderColorField(
            '6. Selected Item Color',
            'Choose the color for the selected item style.',
            mySidebarStyles?.selectedBackgroundColor,
            handleSelectedItemColorChange,
          )}
        </div>
      </form>
      {openConfirmation?.length > 0 && (
      <ConfirmationModal
        show={openConfirmation?.length > 0}
        setOpenFrom={() => { setOpenConfirmation(''); setSaveValues({} as SidebarThemesFormInput); }}
        text={getConfirmationText(openConfirmation)}
        confirmationIcon={<ConfirmationIcon />}
        handleConfirmation={getSuccessRun(openConfirmation)}
        closeBtnText="Cancel"
        proceedBtnText="Proceed"
      />
      )}
    </div>
  );
}

export default SidebarThemes;
